import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GitCompare, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const Delta = ({ v, pct }) => {
  if (!v) return <span className="text-slate-400">0</span>;
  const up = v > 0;
  return <span className={`font-mono font-semibold ${up ? "text-emerald-700" : "text-rose-600"}`}>{up ? "+" : "-"}{rupiah(Math.abs(v))}{pct !== null && pct !== undefined ? ` (${up ? "+" : ""}${pct}%)` : ""}</span>;
};

export default function PayrollCompare({ periodId, version }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/payroll/periods/${periodId}/compare`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [open, periodId, version]);
  const janggal = data ? data.rows.filter((r) => r.janggal) : [];
  const shown = data ? (all ? data.rows : janggal) : [];
  return (
    <Card className="p-0 overflow-hidden" data-testid="payroll-compare">
      <button className="flex w-full items-center gap-2 px-4 py-3 text-left font-heading font-bold text-slate-800 hover:bg-slate-50" onClick={() => setOpen(!open)} data-testid="payroll-compare-toggle">
        <GitCompare className="h-4 w-4 text-orange-500" /> Bandingkan dengan Periode Sebelumnya
        {data && janggal.length > 0 && <span className="ml-2 rounded-full bg-rose-600 px-2 py-0.5 text-xs text-white" data-testid="payroll-compare-count">{janggal.length} perlu dicek</span>}
        <span className="ml-auto">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
      </button>
      {open && data && (
        <div className="border-t">
          {!data.prev ? <div className="p-6 text-center text-sm text-slate-400" data-testid="payroll-compare-none">Belum ada periode sebelumnya untuk dibandingkan.</div> : <>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm bg-slate-50">
              <span>Dibanding <b>{data.prev.nama}</b> ({formatDate(data.prev.tanggal_mulai)} – {formatDate(data.prev.tanggal_selesai)})</span>
              <span className="ml-auto" data-testid="payroll-compare-total">Total: {rupiah(data.total_prev)} → <b>{rupiah(data.total_now)}</b> <Delta v={data.total_now - data.total_prev} pct={data.total_prev ? Math.round((data.total_now - data.total_prev) / data.total_prev * 1000) / 10 : null} /></span>
              <Button size="sm" variant="outline" onClick={() => setAll(!all)} data-testid="payroll-compare-all">{all ? "Hanya yang janggal" : `Tampilkan semua (${data.rows.length})`}</Button>
            </div>
            <div className="overflow-x-auto scroll-thin">
              <table className="w-full text-sm">
                <thead><tr className="bg-blue-900 text-white text-xs uppercase">
                  {["Nama", "Gaji Sebelumnya", "Gaji Sekarang", "Selisih", "Hari", "Lembur", "Tarif/Hari", "Catatan"].map((h) => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {shown.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">Tidak ada kenaikan yang janggal.</td></tr>}
                  {shown.map((r, i) => (
                    <tr key={r.employee_id} className={`border-b border-slate-100 ${r.janggal ? "bg-rose-50/60" : ""}`} data-testid={`payroll-compare-row-${i}`}>
                      <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.nama} <span className="text-xs text-slate-400">{r.kode}</span></td>
                      <td className="px-3 py-2 font-mono">{rupiah(r.gaji_prev)}</td>
                      <td className="px-3 py-2 font-mono font-bold">{rupiah(r.gaji_now)}</td>
                      <td className="px-3 py-2 whitespace-nowrap"><Delta v={r.selisih} pct={r.pct} /></td>
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{r.hari_prev} → {r.hari_now}</td>
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{r.lembur_prev} → {r.lembur_now} j</td>
                      <td className="px-3 py-2 font-mono whitespace-nowrap text-xs">{rupiah(r.tarif_prev)} → {rupiah(r.tarif_now)}</td>
                      <td className="px-3 py-2 text-xs">{r.alasan.map((a) => <span key={a} className="mr-1 inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 font-semibold text-rose-700"><AlertTriangle className="h-3 w-3" />{a}</span>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>}
        </div>
      )}
    </Card>
  );
}
